/**
 * Challenge Detail View Component
 * Shows detailed view of a challenge with all participants and their picks (1X2)
 */

import React, { useMemo } from 'react';
import { View, Text, ScrollView, StyleSheet } from 'react-native';
import { ChallengeWithStats, Prediction, Match, ChallengePick } from '../../types/challenges';
import { getParticipantStats } from '../../helpers/challenge.helper';
import { ZportiBadge } from '../ui/zportiBadge';
import { theme } from '../../theme';

/** Derive prediction-like array from unified picks (marketId 1) for stats/display. */
function predictionsFromPicks(challengeId: string, picks: ChallengePick[]): Prediction[] {
  return picks
    .filter((p) => p.marketId === 1)
    .map((p) => {
      const v = String(p.value ?? '').toLowerCase();
      const outcome = (v === 'home' || v === 'draw' || v === 'away' ? v : 'draw') as 'home' | 'draw' | 'away';
      return {
        id: p.id ?? '',
        challengeId,
        userId: p.userId,
        matchId: p.matchId,
        homeScore: 0,
        awayScore: 0,
        outcome,
        submittedAt: new Date(),
        updatedAt: new Date(),
        locked: false,
        points: p.pointsEarned,
        correct: p.isCorrectOutcome,
      };
    });
}

interface ChallengeDetailViewProps {
  challenge: ChallengeWithStats;
  /** Unified picks (1X2 + other). If provided, predictions are derived from picks (marketId 1). */
  picks?: ChallengePick[];
  /** Legacy: pass predictions directly (e.g. stories). Ignored if picks is provided. */
  predictions?: Prediction[];
  users: Array<{ id: string; name: string; avatar?: string }>;
  matches: Match[];
  testID?: string;
}

export const ChallengeDetailView: React.FC<ChallengeDetailViewProps> = ({
  challenge,
  picks: picksProp,
  predictions: predictionsProp,
  users,
  matches,
  testID,
}) => {
  const predictions = useMemo(() => {
    if (Array.isArray(picksProp) && picksProp.length > 0) {
      return predictionsFromPicks(challenge.id, picksProp);
    }
    return predictionsProp ?? [];
  }, [challenge.id, picksProp, predictionsProp]);

  const participantStats = getParticipantStats(challenge, predictions, users);
  const creatorName = users.find((u) => u.id === challenge.creatorId)?.name ?? 'Unknown';

  // Get matches for this challenge
  const challengeMatches = challenge.matchIds
    .map(matchId => matches.find(m => m.id === matchId))
    .filter((match): match is Match => match !== undefined);

  const getPredictionForUserAndMatch = (userId: string, matchId: string): Prediction | undefined => {
    return predictions.find(
      p => p.challengeId === challenge.id && p.userId === userId && p.matchId === matchId
    );
  };

  const getPredictionBadgeVariant = (prediction: Prediction | undefined, match: Match): 'success' | 'error' | 'warning' | 'info' => {
    if (!prediction || match.status !== 'finished') {
      return 'info';
    }

    if (prediction.points === undefined) {
      return 'info';
    }

    if (prediction.points === 0) {
      return 'error';
    }

    // Check if exact score
    if (prediction.homeScore === match.homeScore && prediction.awayScore === match.awayScore) {
      return 'success';
    }

    // Correct outcome but not exact score
    return 'warning';
  };

  const formatPrediction = (prediction: Prediction | undefined): string => {
    if (!prediction) {
      return 'No prediction';
    }
    return `${prediction.homeScore}-${prediction.awayScore}`;
  };

  const formatMatchResult = (match: Match): string => {
    if (match.status !== 'finished' || match.homeScore === undefined || match.awayScore === undefined) {
      return 'TBD';
    }
    return `${match.homeScore}-${match.awayScore}`;
  };

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false} testID={testID || `challenge-detail-view-${challenge.id}`}>
      {/* Challenge Header */}
      <View style={styles.header}>
        <Text style={styles.challengeName}>{challenge.name}</Text>
        <Text style={styles.challengeDescription}>
          Challenge created by {creatorName}
        </Text>
        <View style={styles.statusRow}>
          <ZportiBadge
            text={challenge.status.toUpperCase()}
            variant={challenge.status === 'active' ? 'success' : 'info'}
            size="medium"
          />
          <Text style={styles.metaText}>
            {challenge.participantCount} participants • {challenge.matchCount} matches
          </Text>
        </View>
      </View>

      {/* Scoring Rules */}
      <View style={styles.scoringSection}>
        <Text style={styles.sectionTitle}>Scoring Rules</Text>
        <View style={styles.scoringRules}>
          <View style={styles.scoringRule}>
            <Text style={styles.scoringIcon}>🎯</Text>
            <Text style={styles.scoringText}>Exact Score: <Text style={styles.scoringValue}>{challenge.scoringRules.exactScore} points</Text></Text>
          </View>
          <View style={styles.scoringRule}>
            <Text style={styles.scoringIcon}>✅</Text>
            <Text style={styles.scoringText}>Correct Outcome: <Text style={styles.scoringValue}>{challenge.scoringRules.correctOutcome} point</Text></Text>
          </View>
        </View>
      </View>

      {/* Leaderboard */}
      <View style={styles.leaderboardSection}>
        <Text style={styles.sectionTitle}>🏆 Leaderboard</Text>
        {participantStats.map((participant, index) => (
          <View
            key={participant.userId}
            style={[
              styles.participantRow,
              index === 0 && styles.firstPlace,
              index === 1 && styles.secondPlace,
              index === 2 && styles.thirdPlace,
            ]}
          >
            <View style={styles.rankSection}>
              <Text style={[
                styles.rankText,
                index === 0 && styles.firstPlaceText,
              ]}>
                #{participant.rank}
              </Text>
              {participant.avatar && (
                <Text style={styles.avatar}>{participant.avatar}</Text>
              )}
            </View>
            <Text style={styles.participantName}>{participant.userName}</Text>
            <View style={styles.pointsSection}>
              <Text style={styles.pointsValue}>{participant.totalPoints}</Text>
              <Text style={styles.pointsLabel}>odds</Text>
            </View>
          </View>
        ))}
      </View>

      {/* Matches and Predictions Grid */}
      <View style={styles.predictionsSection}>
        <Text style={styles.sectionTitle}>⚽ Matches & Predictions</Text>
        {challengeMatches.map((match) => (
          <View key={match.id} style={styles.matchCard}>
            {/* Match Header */}
            <View style={styles.matchHeader}>
              <View style={styles.matchInfo}>
                <Text style={styles.matchTeams}>
                  {match.homeTeam} vs {match.awayTeam}
                </Text>
                <Text style={styles.matchLeague}>{match.league}</Text>
              </View>
              <ZportiBadge
                text={
                  match.status === 'finished' ? 'FT' :
                  match.status === 'live' ? 'LIVE' :
                  'UPCOMING'
                }
                variant={
                  match.status === 'finished' ? 'info' :
                  match.status === 'live' ? 'error' :
                  'status'
                }
                size="medium"
              />
            </View>

            {/* Match Result */}
            {match.status === 'finished' && (
              <View style={styles.matchResult}>
                <Text style={styles.matchResultLabel}>Result:</Text>
                <Text style={styles.matchResultScore}>{formatMatchResult(match)}</Text>
              </View>
            )}

            {/* Predictions Table */}
            <View style={styles.predictionsTable}>
              <View style={styles.tableHeader}>
                <Text style={styles.tableHeaderText}>Player</Text>
                <Text style={styles.tableHeaderText}>Prediction</Text>
                <Text style={styles.tableHeaderText}>Score</Text>
              </View>
              {participantStats.map((participant) => {
                const prediction = getPredictionForUserAndMatch(participant.userId, match.id);
                const badgeVariant = getPredictionBadgeVariant(prediction, match);

                return (
                  <View key={participant.userId} style={styles.tableRow}>
                    <View style={styles.playerCell}>
                      {participant.avatar && (
                        <Text style={styles.playerAvatar}>{participant.avatar}</Text>
                      )}
                      <Text style={styles.playerName}>{participant.userName}</Text>
                    </View>
                    <View style={styles.predictionCell}>
                      {prediction ? (
                        <ZportiBadge
                          text={formatPrediction(prediction)}
                          variant={badgeVariant}
                          size="medium"
                        />
                      ) : (
                        <Text style={styles.noPrediction}>—</Text>
                      )}
                    </View>
                    <View style={styles.pointsCell}>
                      <Text style={[
                        styles.pointsCellText,
                        (prediction?.points ?? 0) > 0 && styles.pointsPositive
                      ]}>
                        {prediction?.points !== undefined ? `+${prediction.points}` : '—'}
                      </Text>
                    </View>
                  </View>
                );
              })}
            </View>
          </View>
        ))}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.white,
  },
  header: {
    padding: 20,
    backgroundColor: theme.primary,
  },
  challengeName: {
    fontSize: 24,
    fontWeight: '700',
    color: theme.white,
    marginBottom: 8,
  },
  challengeDescription: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.9)',
    marginBottom: 12,
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  metaText: {
    fontSize: 13,
    color: 'rgba(255, 255, 255, 0.8)',
  },
  scoringSection: {
    padding: 16,
    backgroundColor: theme.gray[100],
    borderBottomWidth: 1,
    borderBottomColor: theme.gray[200],
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: theme.gray[900],
    marginBottom: 12,
  },
  scoringRules: {
    gap: 8,
  },
  scoringRule: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  scoringIcon: {
    fontSize: 20,
  },
  scoringText: {
    fontSize: 14,
    color: theme.gray[700],
  },
  scoringValue: {
    fontWeight: '700',
    color: theme.gray[900],
  },
  leaderboardSection: {
    padding: 16,
    backgroundColor: theme.white,
    borderBottomWidth: 1,
    borderBottomColor: theme.gray[200],
  },
  participantRow: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    backgroundColor: theme.white,
    borderRadius: 8,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: theme.gray[200],
  },
  firstPlace: {
    backgroundColor: '#FFF9E6',
    borderColor: '#FFD700',
    borderWidth: 2,
  },
  secondPlace: {
    backgroundColor: '#F5F5F5',
    borderColor: '#C0C0C0',
  },
  thirdPlace: {
    backgroundColor: '#FFF5F0',
    borderColor: '#CD7F32',
  },
  rankSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginRight: 12,
  },
  rankText: {
    fontSize: 16,
    fontWeight: '700',
    color: theme.gray[600],
    width: 30,
  },
  firstPlaceText: {
    color: '#FFD700',
    fontSize: 18,
  },
  avatar: {
    fontSize: 24,
  },
  participantName: {
    flex: 1,
    fontSize: 15,
    fontWeight: '600',
    color: theme.gray[900],
  },
  pointsSection: {
    alignItems: 'flex-end',
  },
  pointsValue: {
    fontSize: 18,
    fontWeight: '700',
    color: theme.success,
  },
  pointsLabel: {
    fontSize: 11,
    color: theme.gray[500],
  },
  predictionsSection: {
    padding: 16,
  },
  matchCard: {
    backgroundColor: theme.white,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: theme.gray[200],
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  matchHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: theme.gray[100],
  },
  matchInfo: {
    flex: 1,
  },
  matchTeams: {
    fontSize: 15,
    fontWeight: '700',
    color: theme.gray[900],
    marginBottom: 4,
  },
  matchLeague: {
    fontSize: 12,
    color: theme.primary,
    fontStyle: 'italic',
  },
  matchResult: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    backgroundColor: theme.gray[100],
    borderRadius: 8,
    marginBottom: 12,
    gap: 8,
  },
  matchResultLabel: {
    fontSize: 13,
    color: theme.gray[600],
    fontWeight: '600',
  },
  matchResultScore: {
    fontSize: 20,
    fontWeight: '700',
    color: theme.gray[900],
    letterSpacing: 1,
  },
  predictionsTable: {
    gap: 4,
  },
  tableHeader: {
    flexDirection: 'row',
    paddingVertical: 8,
    paddingHorizontal: 4,
    backgroundColor: theme.gray[100],
    borderRadius: 6,
    marginBottom: 8,
  },
  tableHeaderText: {
    fontSize: 11,
    fontWeight: '700',
    color: theme.gray[600],
    textTransform: 'uppercase',
    flex: 1,
  },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 4,
    borderBottomWidth: 1,
    borderBottomColor: theme.gray[100],
  },
  playerCell: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  playerAvatar: {
    fontSize: 16,
  },
  playerName: {
    fontSize: 13,
    color: theme.gray[900],
    fontWeight: '500',
  },
  predictionCell: {
    flex: 1,
    alignItems: 'center',
  },
  noPrediction: {
    fontSize: 13,
    color: theme.gray[400],
  },
  pointsCell: {
    flex: 1,
    alignItems: 'flex-end',
  },
  pointsCellText: {
    fontSize: 13,
    fontWeight: '600',
    color: theme.gray[500],
  },
  pointsPositive: {
    color: theme.success,
  },
});

